import React, { ReactElement } from 'react';

import PropTypes from 'prop-types';

import Avatar, { DefaultAvatar } from '@adsk/alloy-react-avatar';
import { useRelativeDate } from '@adsk/alloy-react-datetime';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { getTooltipDate } from './helpers';
import { CommentText, CommentTextProps } from './layout';

const defaultRenderCommentText = (props: CommentTextProps) => (
  <CommentText {...props} />
);

type CommentCardProps = StylableComponent<HTMLDivElement> & {
  comment: string;
  user: { name: string; defaultAvatar?: boolean; imageUrl?: string | null };
  date: Date;
  startDate?: Date;
  renderCommentText?: (props: CommentTextProps) => ReactElement;
};

/**
 * A CommentCard component which displays the user which created the comment, it's avatar,
 * and the date it was submitted.
 */
const CommentCard = React.forwardRef<HTMLDivElement, CommentCardProps>(
  (
    {
      className,
      style,
      user,
      renderCommentText = defaultRenderCommentText,
      comment,
      date,
      startDate,
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            border: `1px solid ${theme.tokens.colors.border.subtle.value}`,
            borderRadius: theme.borderRadius.medium,
            backgroundColor: theme.tokens.colors.background.primary.value,
          },
          style,
        ]}
      >
        <div
          css={[
            theme.typography.caption,
            {
              backgroundColor: theme.tokens.colors.background.secondary.value,
              color: theme.tokens.colors.text.primary.value,
              display: 'flex',
              justifyContent: 'space-between',
              padding: theme.spacing.XS,
              borderTopRightRadius: '4px',
              borderTopLeftRadius: '4px',
            },
          ]}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {user.defaultAvatar ? (
              <DefaultAvatar size={Avatar.SIZES.SMALL_16} />
            ) : (
              <Avatar
                size={Avatar.SIZES.SMALL_16}
                name={user.name}
                imageUrl={user.imageUrl}
                color={Avatar.COLORS.COLOR_3}
              />
            )}
            <span
              style={{
                marginLeft: theme.spacing.XXS,
              }}
            >
              {user.name}
            </span>
          </div>
          <Tooltip content={getTooltipDate(date, T)}>
            {useRelativeDate(date, startDate)}
          </Tooltip>
        </div>
        {renderCommentText({ comment })}
      </div>
    );
  },
);
CommentCard.displayName = 'CommentCard';
CommentCard.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The comment's user */
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
  }).isRequired,
  /** Render custom comment text. Props ({ comment }) **/
  renderCommentText: PropTypes.func,
  /** The comment text **/
  comment: PropTypes.string.isRequired,
  /** The date of the comment **/
  date: PropTypes.instanceOf(Date).isRequired,
  /** Sets different start date to compare date to **/
  startDate: PropTypes.instanceOf(Date),
};
export default CommentCard;
